import Form from "@/components/Form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";

import DescriptionList from "@/components/description-list/DescriptionList";
import Field from "@/components/Field";
import Fieldset from "@/components/Fieldset";
import PageContent from "@/components/PageContent";
import SaveButton from "@/components/SaveButton";
import Section from "@/components/Section";
import { Textarea } from "@/components/ui/textarea";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { isRequired } from "@/lib/form";
import { writeShiftReport, writeShiftReportBase } from "@/schemas/shiftReport";
import { ApiError } from "@/services/api";
import { ReadShiftReport, WriteShiftReport } from "@/types/shiftReport";
import { ReadShiftReportDepartmentEntry } from "@/types/shiftReportDepartmentEntry";
import { UseMutateFunction } from "@tanstack/react-query";
import { Table } from "@tanstack/react-table";
import { CalendarIcon, ReplaceAll } from "lucide-react";
import { useForm } from "react-hook-form";
import ShiftReportDepartmentEntriesEditor from "./ShiftReportEntryEditor";

interface ShiftReportFormProps {
  data?: Partial<ReadShiftReport>;
  onSubmit: UseMutateFunction<ReadShiftReport, ApiError, WriteShiftReport>;
  table: Table<ReadShiftReportDepartmentEntry>;
  disabled?: boolean;
}

const defaultValues: Partial<ReadShiftReport> = {
  comments: "",
  department_entries: [],
};

const ShiftReportForm = ({
  data = defaultValues,
  table,
  onSubmit,
  disabled,
}: ShiftReportFormProps) => {
  const { t } = useTranslation();

  const form = useForm<WriteShiftReport>({
    resolver: zodResolver(writeShiftReport),
    defaultValues: data as WriteShiftReport,
    mode: "all",
    disabled,
  });

  useFormUnsavedChangesWarning(form);

  const handleSubmit = (values: WriteShiftReport) => {
    onSubmit(values);
    form.reset(values);
  };

  return (
    <Form form={form} handleSubmit={handleSubmit}>
      <PageContent
        icon={<ReplaceAll className="w-6 h-6 mr-3" />}
        footer={<SaveButton />}
        title={t("shiftReports.title.single")}
      >
        <div className="grid grid-cols-1 h-full auto-rows-min">
          <div>
            <Section title={t("shiftReports.sections.data")}>
              <DescriptionList
                data={data}
                translationKey="shiftReports"
                variant="ghost"
                className="py-4"
              >
                <DescriptionList.Content className="p-0">
                  <DescriptionList.Item name="store_name" />
                  <DescriptionList.Item name="shift_date" icon={CalendarIcon} />
                  <DescriptionList.Item name="shift_name" />
                </DescriptionList.Content>
              </DescriptionList>
            </Section>
            <Section>
              <Fieldset className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Field
                  name="comments"
                  label={t("shiftReports.fields.comments")}
                  required={isRequired(writeShiftReportBase.shape.comments)}
                  render={({ field }) => (
                    <Textarea className="w-full" {...field} />
                  )}
                />
              </Fieldset>
            </Section>
          </div>
          <Section className="space-y-4 flex flex-col h-full mt-0 xl:mt-4">
            <div className="space-y-3 grow shrink-0">
              <ShiftReportDepartmentEntriesEditor table={table} />
            </div>
          </Section>
        </div>
      </PageContent>
    </Form>
  );
};

export default ShiftReportForm;
